import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

// Composant mémorisé pour le formulaire de pays
const CountryForm = React.memo(({ initialCountry, onAdd }) => {
    const { t } = useLanguage();
    const [localCountry, setLocalCountry] = useState(initialCountry);

    useEffect(() => {
        setLocalCountry(initialCountry);
    }, [initialCountry]);

    const handleAdd = () => {
        onAdd(localCountry);
        setLocalCountry({ name: '', code: '', cities: [], delivery_time_days: 7, currency: 'XAF', timezone: '' });
    };

    return (
        <div className="bg-card p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">🏴 {t('addDeliveryCountry')}</h3>
            <div className="grid grid-cols-2 gap-4">
                <input
                    type="text"
                    placeholder={t('countryNamePlaceholder')}
                    value={localCountry.name}
                    onChange={(e) => setLocalCountry(prev => ({...prev, name: e.target.value}))}
                    className="border border-border p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                />
                <input
                    type="text"
                    placeholder={t('countryCodePlaceholder')}
                    value={localCountry.code}
                    onChange={(e) => setLocalCountry(prev => ({...prev, code: e.target.value.toUpperCase()}))}
                    className="border border-border p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                />
                <input
                    type="number"
                    placeholder={t('deliveryTimePlaceholder')}
                    value={localCountry.delivery_time_days}
                    onChange={(e) => setLocalCountry(prev => ({...prev, delivery_time_days: parseInt(e.target.value) || 0}))}
                    className="border border-border p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                />
                <select
                    value={localCountry.currency}
                    onChange={(e) => setLocalCountry(prev => ({...prev, currency: e.target.value}))}
                    className="border border-border p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                >
                    <option value="XAF">XAF (Franc CFA)</option>
                    <option value="EUR">EUR (Euro)</option>
                    <option value="USD">USD (Dollar)</option>
                    <option value="XOF">XOF (Franc CFA Ouest)</option>
                </select>
            </div>
            <button
                onClick={handleAdd}
                className="mt-4 bg-accent text-white px-4 py-2 rounded hover:bg-accent/90"
            >
                ➕ {t('addCountryBtn')}
            </button>
        </div>
    );
});

CountryForm.displayName = 'CountryForm';

const GeographicManagement = () => {
    const { t } = useLanguage();
    const [activeTab, setActiveTab] = useState('countries');
    const [countries, setCountries] = useState([]);
    const [zones, setZones] = useState([]);
    const [localPartners, setLocalPartners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [newCountry, setNewCountry] = useState({
        name: '',
        code: '',
        cities: [],
        delivery_time_days: 7,
        currency: 'XAF',
        timezone: ''
    });
    const [newCity, setNewCity] = useState({ name: '', code: '', delivery_time_days: 2 });
    const [selectedCountryForCities, setSelectedCountryForCities] = useState('');
    const [newZone, setNewZone] = useState({
        zone_name: '',
        country_code: '',
        cities: [],
        is_country_wide: false
    });
    const [customCountries, setCustomCountries] = useState({});
    const [newCustomCountry, setNewCustomCountry] = useState('');
    const [newCustomCities, setNewCustomCities] = useState('');

    useEffect(() => {
        fetchGeographicData();
        fetchCustomCountries();
    }, []);

    const fetchGeographicData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            
            // Récupérer les pays
            const countriesResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/geographic/countries`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (countriesResponse.ok) {
                const countriesData = await countriesResponse.json();
                setCountries(countriesData.delivery_countries || []);
            }

            // Récupérer les zones
            const zonesResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/geographic/zones`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (zonesResponse.ok) {
                const zonesData = await zonesResponse.json();
                setZones(zonesData.geographic_zones || []);
            }

            // Récupérer les LOCAL_PARTNER
            const partnersResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/local-partners/assignments`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (partnersResponse.ok) {
                const partnersData = await partnersResponse.json();
                setLocalPartners(partnersData.local_partners || []);
            }
        } catch (error) {
            console.error('Erreur récupération données géographiques:', error);
        }
        setLoading(false);
    };

    const addCountry = async (countryData) => {
        if (!countryData.name || !countryData.code) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/geographic/countries`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(countryData)
            });

            if (response.ok) {
                fetchGeographicData();
            }
        } catch (error) {
            console.error('Erreur ajout pays:', error);
        }
    };

    const addCityToCountry = async () => {
        if (!newCity.name || !selectedCountryForCities) return;

        try {
            const country = countries.find(c => c.code === selectedCountryForCities);
            if (!country) return;

            const updatedCities = [...country.cities, { ...newCity, is_active: true }];

            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/geographic/countries/${selectedCountryForCities}/cities`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ cities: updatedCities })
            });

            if (response.ok) {
                setNewCity({ name: '', code: '', delivery_time_days: 2 });
                fetchGeographicData();
            }
        } catch (error) {
            console.error('Erreur ajout ville:', error);
        }
    };

    const createZone = async (zoneData) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/geographic/zones`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(zoneData)
            });

            if (response.ok) {
                fetchGeographicData();
            }
        } catch (error) {
            console.error('Erreur création zone:', error);
        }
    };

    const assignPartnerToZone = async (partnerId, assignment) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${partnerId}/geographic-assignment`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(assignment)
            });

            if (response.ok) {
                fetchGeographicData();
            }
        } catch (error) {
            console.error('Erreur assignation partenaire:', error);
        }
    };

    const fetchCustomCountries = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/countries/custom`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setCustomCountries(data.custom_countries || {});
            }
        } catch (error) {
            console.error('Erreur récupération pays custom:', error);
        }
    };

    const addCustomCountry = async () => {
        const country = newCustomCountry.trim();
        if (!country) return;

        const cities = newCustomCities
            .split(',')
            .map(c => c.trim())
            .filter(Boolean);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/countries/custom`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ country, cities })
            });

            if (response.ok) {
                setNewCustomCountry('');
                setNewCustomCities('');
                fetchCustomCountries();
            }
        } catch (error) {
            console.error('Erreur ajout pays custom:', error);
        }
    };

    const deleteCustomCountry = async (countryName) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/countries/custom/${encodeURIComponent(countryName)}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );

            if (response.ok) {
                fetchCustomCountries();
            }
        } catch (error) {
            console.error('Erreur suppression pays custom:', error);
        }
    };

    const CountriesTab = () => (
        <div className="space-y-6">
            <CountryForm 
                initialCountry={newCountry}
                onAdd={addCountry}
            />

            <div className="bg-card p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">🏙️ {t('addCity')}</h3>
                <div className="grid grid-cols-3 gap-4">
                    <select
                        value={selectedCountryForCities}
                        onChange={(e) => setSelectedCountryForCities(e.target.value)}
                        className="border p-2 rounded"
                    >
                        <option value="">{t('selectCountry')}</option>
                        {countries.map(country => (
                            <option key={country.code} value={country.code}>
                                {country.name} ({country.code})
                            </option>
                        ))}
                    </select>
                    <input
                        key="city-name-input"
                        type="text"
                        placeholder={t('cityNamePlaceholder')}
                        value={newCity.name}
                        onChange={(e) => {
                            const value = e.target.value;
                            setNewCity(prev => ({...prev, name: value}));
                        }}
                        className="border border-border p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                    <input
                        key="city-code-input"
                        type="text"
                        placeholder={t('cityCodePlaceholder')}
                        value={newCity.code}
                        onChange={(e) => {
                            const value = e.target.value.toUpperCase();
                            setNewCity(prev => ({...prev, code: value}));
                        }}
                        className="border border-border p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                </div>
                <button
                    onClick={addCityToCountry}
                    disabled={!selectedCountryForCities || !newCity.name}
                    className="mt-4 bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:bg-gray-400"
                >
                    ➕ {t('addCityBtn')}
                </button>
            </div>

            <div className="bg-card p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">📊 {t('configuredDeliveryCountries')}</h3>
                <div className="space-y-4">
                    {countries.map(country => (
                        <div key={country.code} className="border p-4 rounded">
                            <div className="flex justify-between items-center mb-2">
                                <h4 className="font-semibold">🏴 {country.name} ({country.code})</h4>
                                <span className="text-sm text-muted-foreground">
                                    {country.delivery_time_days} {t('days')} • {country.currency}
                                </span>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-sm">
                                {country.cities?.map(city => (
                                    <div key={city.name} className="bg-muted p-2 rounded">
                                        📍 {city.name} ({city.code}) - {city.delivery_time_days}j
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="bg-card p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">🌐 {t('customCountries')}</h3>
                <p className="text-sm text-muted-foreground mb-4">
                    {t('customCountriesDescription')}
                </p>

                <div className="grid grid-cols-2 gap-4 mb-4">
                    <input
                        type="text"
                        placeholder={t('customCountryNamePlaceholder')}
                        value={newCustomCountry}
                        onChange={(e) => setNewCustomCountry(e.target.value)}
                        className="border border-border p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                    <input
                        type="text"
                        placeholder={t('customCitiesPlaceholder')}
                        value={newCustomCities}
                        onChange={(e) => setNewCustomCities(e.target.value)}
                        className="border border-border p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                </div>
                <button
                    onClick={addCustomCountry}
                    disabled={!newCustomCountry.trim()}
                    className="mb-6 bg-accent text-white px-4 py-2 rounded hover:bg-indigo-600 disabled:bg-gray-400"
                >
                    ➕ {t('addCountryBtn')}
                </button>

                {Object.keys(customCountries).length > 0 ? (
                    <div className="space-y-3">
                        {Object.entries(customCountries).map(([country, cities]) => (
                            <div key={country} className="border p-4 rounded flex justify-between items-start">
                                <div>
                                    <h4 className="font-semibold">🌍 {country}</h4>
                                    {cities.length > 0 && (
                                        <p className="text-sm text-muted-foreground mt-1">
                                            📍 {cities.join(', ')}
                                        </p>
                                    )}
                                </div>
                                <button
                                    onClick={() => deleteCustomCountry(country)}
                                    className="text-red-500 hover:text-red-700 text-sm px-3 py-1 border border-red-300 rounded hover:bg-red-50"
                                >
                                    {t('delete')}
                                </button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-muted-foreground italic">{t('noCustomCountries')}</p>
                )}
            </div>
        </div>
    );

    const ZonesTab = () => {
        const handleCreateZone = () => {
            if (newZone.zone_name && newZone.country_code) {
                createZone(newZone);
                setNewZone({ zone_name: '', country_code: '', cities: [], is_country_wide: false });
            }
        };

        return (
            <div className="space-y-6">
                <div className="bg-card p-6 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-4">🎯 {t('createGeographicZone')}</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <input
                            key="zone-name-input"
                            type="text"
                            placeholder={t('zoneNamePlaceholder')}
                            value={newZone.zone_name}
                            onChange={(e) => {
                                const value = e.target.value;
                                setNewZone(prev => ({...prev, zone_name: value}));
                            }}
                            className="border p-2 rounded"
                        />
                        <select
                            key="zone-country-select"
                            value={newZone.country_code}
                            onChange={(e) => {
                                const value = e.target.value;
                                setNewZone(prev => ({...prev, country_code: value}));
                            }}
                            className="border p-2 rounded"
                        >
                            <option value="">{t('selectCountry')}</option>
                            {countries.map(country => (
                                <option key={country.code} value={country.code}>
                                    {country.name} ({country.code})
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="mt-4">
                        <label className="flex items-center">
                            <input
                                key="zone-country-wide-checkbox"
                                type="checkbox"
                                checked={newZone.is_country_wide}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setNewZone(prev => ({...prev, is_country_wide: checked}));
                                }}
                                className="mr-2"
                            />
                            {t('countryWideZone')}
                        </label>
                    </div>
                    <button
                        onClick={handleCreateZone}
                        className="mt-4 bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600"
                    >
                        ➕ {t('createZoneBtn')}
                    </button>
                </div>

                <div className="bg-card p-6 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-4">📊 {t('configuredZones')}</h3>
                    <div className="space-y-3">
                        {zones.map(zone => (
                            <div key={zone.zone_id} className="border p-3 rounded">
                                <div className="flex justify-between items-center">
                                    <h4 className="font-semibold">🎯 {zone.zone_name}</h4>
                                    <span className="text-sm text-muted-foreground">
                                        {zone.country_code} • {zone.is_country_wide ? t('wholeCountry') : `${zone.cities?.length || 0} ${t('cities')}`}
                                    </span>
                                </div>
                                {zone.cities?.length > 0 && (
                                    <div className="text-sm text-muted-foreground mt-1">
                                        {t('citiesLabel')}: {zone.cities.join(', ')}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const [partnerAssignment, setPartnerAssignment] = useState({});

    const getPartnerAssignment = (partnerId) => {
        return partnerAssignment[partnerId] || { country: '', cities: [] };
    };

    const updatePartnerAssignment = (partnerId, field, value) => {
        setPartnerAssignment(prev => ({
            ...prev,
            [partnerId]: { ...getPartnerAssignment(partnerId), [field]: value }
        }));
    };

    const togglePartnerCity = (partnerId, cityName) => {
        const current = getPartnerAssignment(partnerId);
        const cities = current.cities.includes(cityName)
            ? current.cities.filter(c => c !== cityName)
            : [...current.cities, cityName];
        updatePartnerAssignment(partnerId, 'cities', cities);
    };

    const handleDirectAssign = (partnerId) => {
        const assignment = getPartnerAssignment(partnerId);
        if (!assignment.country) return;
        assignPartnerToZone(partnerId, {
            assigned_countries: [assignment.country],
            assigned_cities: assignment.cities,
            is_country_wide: assignment.cities.length === 0
        });
        setPartnerAssignment(prev => {
            const next = { ...prev };
            delete next[partnerId];
            return next;
        });
    };

    const PartnersTab = () => (
        <div className="space-y-6">
            <div className="bg-card p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">👥 {t('localPartnersAssignments')}</h3>
                <div className="space-y-4">
                    {localPartners.map(partner => {
                        const assignment = getPartnerAssignment(partner.id);
                        const selectedCountry = countries.find(c => c.code === assignment.country);
                        const citiesForCountry = selectedCountry?.cities || [];

                        return (
                            <div key={partner.id} className="border p-4 rounded">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h4 className="font-semibold">👤 {partner.full_name}</h4>
                                        <p className="text-sm text-muted-foreground">📧 {partner.email}</p>
                                        <p className="text-sm text-muted-foreground">🏙️ {partner.city}, {partner.country}</p>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-sm">
                                            <strong>{t('zoneLabel')}:</strong> {partner.assigned_zone_id || t('notAssigned')}
                                        </div>
                                        <div className="text-sm">
                                            <strong>{t('countryLabel')}:</strong> {partner.assigned_countries?.join(', ') || t('none')}
                                        </div>
                                        <div className="text-sm">
                                            <strong>{t('citiesLabel')}:</strong> {partner.assigned_cities?.length > 0
                                                ? partner.assigned_cities.join(', ')
                                                : (partner.assigned_countries?.length > 0 ? t('wholeCountry') : t('noneF'))}
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-3 pt-3 border-t">
                                    <h5 className="text-sm font-semibold mb-2">{t('editAssignment')}:</h5>

                                    {/* Zone assignment */}
                                    <div className="flex items-center gap-2 mb-3">
                                        <select
                                            onChange={(e) => {
                                                if (e.target.value) {
                                                    const zone = zones.find(z => z.zone_id === e.target.value);
                                                    if (zone) {
                                                        assignPartnerToZone(partner.id, {
                                                            zone_id: zone.zone_id,
                                                            assigned_countries: [zone.country_code],
                                                            assigned_cities: zone.cities,
                                                            is_country_wide: zone.is_country_wide
                                                        });
                                                    }
                                                }
                                            }}
                                            className="border p-1 rounded text-sm"
                                        >
                                            <option value="">{t('assignToZone')}</option>
                                            {zones.map(zone => (
                                                <option key={zone.zone_id} value={zone.zone_id}>
                                                    {zone.zone_name}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="text-xs text-muted-foreground">
                                            {partner.is_active ? `✅ ${t('active')}` : `❌ ${t('inactive')}`}
                                        </div>
                                    </div>

                                    {/* Direct country + cities assignment */}
                                    <div className="bg-muted/50 p-3 rounded space-y-3">
                                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                            Assignation directe pays + villes
                                        </p>
                                        <select
                                            value={assignment.country}
                                            onChange={(e) => {
                                                updatePartnerAssignment(partner.id, 'country', e.target.value);
                                                updatePartnerAssignment(partner.id, 'cities', []);
                                            }}
                                            className="border p-2 rounded text-sm w-full"
                                        >
                                            <option value="">{t('selectCountry')}</option>
                                            {countries.map(country => (
                                                <option key={country.code} value={country.code}>
                                                    {country.name} ({country.code})
                                                </option>
                                            ))}
                                        </select>

                                        {assignment.country && citiesForCountry.length > 0 && (
                                            <div>
                                                <p className="text-xs text-muted-foreground mb-2">
                                                    Villes (laisser vide = pays entier) :
                                                </p>
                                                <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                                                    {citiesForCountry.map(city => (
                                                        <label key={city.name} className="flex items-center gap-1.5 text-sm cursor-pointer hover:bg-muted p-1 rounded">
                                                            <input
                                                                type="checkbox"
                                                                checked={assignment.cities.includes(city.name)}
                                                                onChange={() => togglePartnerCity(partner.id, city.name)}
                                                                className="rounded"
                                                            />
                                                            {city.name}
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {assignment.country && (
                                            <button
                                                onClick={() => handleDirectAssign(partner.id)}
                                                className="bg-accent text-white px-4 py-1.5 rounded text-sm hover:bg-accent/90"
                                            >
                                                Assigner {assignment.cities.length > 0
                                                    ? `${assignment.cities.length} ville(s)`
                                                    : 'pays entier'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    if (loading) {
        return <div className="p-6">🔄 {t('loadingGeographicData')}</div>;
    }

    return (
        <div className="p-6">
            <h2 className="text-2xl font-bold mb-6">🌍 {t('geographicManagement')}</h2>
            
            <div className="mb-6">
                <nav className="flex space-x-4">
                    <button
                        onClick={() => setActiveTab('countries')}
                        className={`px-4 py-2 rounded ${activeTab === 'countries' 
                            ? 'bg-accent text-white' 
                            : 'bg-muted text-foreground hover:bg-gray-300'}`}
                    >
                        🏴 {t('countriesAndCities')}
                    </button>
                    <button
                        onClick={() => setActiveTab('zones')}
                        className={`px-4 py-2 rounded ${activeTab === 'zones' 
                            ? 'bg-accent text-white' 
                            : 'bg-muted text-foreground hover:bg-gray-300'}`}
                    >
                        🎯 {t('geographicZones')}
                    </button>
                    <button
                        onClick={() => setActiveTab('partners')}
                        className={`px-4 py-2 rounded ${activeTab === 'partners' 
                            ? 'bg-accent text-white' 
                            : 'bg-muted text-foreground hover:bg-gray-300'}`}
                    >
                        👥 {t('localPartners')}
                    </button>
                </nav>
            </div>

            <div className="min-h-screen">
                {activeTab === 'countries' && <CountriesTab />}
                {activeTab === 'zones' && <ZonesTab />}
                {activeTab === 'partners' && <PartnersTab />}
            </div>
        </div>
    );
};

export default GeographicManagement;